import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

type Language = 'ru' | 'en';

// Define recursive type for translation structure to safely traverse it
type TranslationNode = string | { [key: string]: TranslationNode };

const translations = {
    ru: {
        nav: {
            catalog: 'Каталог',
            about: 'О нас',
            contacts: 'Контакты',
            profile: 'Личный кабинет',
            login: 'Войти',
            cart: 'Корзина',
        },
        common: {
            searchPlaceholder: 'Поиск...',
            found: 'Найдено',
            items: 'товаров',
            reset: 'Сбросить',
            buy: 'Купить',
            inCart: 'В корзине',
            outOfStock: 'Нет в наличии',
            new: 'New',
            loading: 'Загрузка...',
            save: 'Сохранить',
            saved: 'Сохранено',
            cancel: 'Отмена',
            currency: '₽',
            goHome: 'На главную',
            tryAgain: 'Попробовать снова',
            city: 'Город',
            edit: 'Изменить',
            phone: 'Телефон',
            address: 'Адрес',
            last_name: 'Фамилия',
            middle_name: 'Отчество',
            first_name: 'Имя',
            company: 'Компания',
            inn: 'ИНН',
            kpp: 'КПП',
            email: 'Email адрес',
            close: 'Закрыть',
            comment_order: 'Комментарий к заказу',
            password: 'Пароль',
            password_confirmation: 'Повторите пароль',
            back: 'Назад',
            addToCart: 'Добавить в корзину',
            reviews: 'Отзывы',
            specifications: 'Характеристики',
            description: 'Описание',
            similarProducts: 'Похожие товары',
        },
        product: {
            brand: 'Бренд',
            style: 'Стиль',
            abv: 'Алкоголь',
            ibu: 'Горечь',
            plato: 'Плотность',
            volume: 'Объем',
            quantity: 'Количество',
            total: 'Итого',
        },
        filters: {
            title: 'Фильтры',
            price: 'Цена',
            abv: 'Крепость (ABV)',
            ibu: 'Горечь (IBU)',
            category: 'Категория',
            manufacturer: 'Производитель',
            style: 'Стиль',
            resetFilters: 'Сбросить фильтры',
            search: 'Поиск...',
            nothingFound: 'Ничего не найдено',
        },
        sort: {
            created_at: 'Новые сначала',
            priceAZ: 'От дешевого к дорогому',
            priceZA: 'От дорогого к дешевому',
            titleAZ: 'По названию (А-Я)',
            titleZA: 'По названию (Я-А)',
            breweryAZ: 'По производителю (А-Я)',
            breweryZA: 'По производителю (Я-А)',
            untappdAZ: 'По рейтингу (А-Я)',
            untappdZA: 'По рейтингу (Я-А)',
            styleAZ: 'По стилю (А-Я)',
            styleZA: 'По стилю (Я-А)',
            tareAZ: 'По таре (А-Я)',
            tareZA: 'По таре (Я-А)',
            volumeAZ: 'По объему (А-Я)',
            volumeZA: 'По объему (Я-А)',
        },
        profile: {
            personalInformation: 'Личная информация',
            managePersonalDataAndAddress:
                'Управляйте вашими личными данными и адресом доставки',
            viewPastPurchaseDetails:
                'Просматривайте детали ваших прошлых покупок',
            stats: {
                totalOrders: 'Всего заказов',
                totalSpent: 'Сумма покупок',
                avgCheck: 'Средний чек',
                lastOrder: 'Последний заказ',
            },
        },
        auth: {
            loginTitle: 'Вход',
            registerTitle: 'Регистрация',
            emailLabel: 'Email',
            passwordLabel: 'Пароль',
            nameLabel: 'Имя',
            confirmPasswordLabel: 'Подтверждение пароля',
            loginButton: 'Войти',
            registerButton: 'Создать аккаунт',
            noAccount: 'Нет аккаунта?',
            hasAccount: 'Уже есть аккаунт?',
            toRegister: 'Зарегистрироваться',
            toLogin: 'Войти',
            rememberMe: 'Запомнить меня',
            forgotPassword: 'Забыли пароль?',
            resetPasswordTitle: 'Сброс пароля',
            resetPasswordDesc: 'Введите email для получения ссылки',
            sendResetLink: 'Отправить ссылку',
            backToLogin: 'Вернуться ко входу',
            resetLinkSent: 'Ссылка отправлена!',
            ResendVerificationEmail: 'Отправить письмо повторно',
            yourPassword: 'Ваш пароль',
            setNewPasswordDesc: 'Пожалуйста, введите новый пароль ниже',
            newPasswordLabel: 'Пароль',
            setNewPasswordBtn: 'Сбросить пароль',
            resetSuccess: 'Пароль успешно изменен',
            setNewPasswordTitle: 'Сбросить пароль',
            errors: {
                required: 'Поле обязательно',
                email: 'Некорректный Email',
                passwordShort: 'Минимум 6 символов',
                match: 'Пароли не совпадают',
                fail: 'Ошибка авторизации',
            },
        },
        errors: {
            e404: {
                title: 'Страница не найдена',
                desc: 'Похоже, это пиво уже выпили. Страница, которую вы ищете, не существует или была перемещена.',
            },
            e403: {
                title: 'Доступ запрещен',
                desc: 'У вас нет доступа к этой странице. Возможно, ссылка повреждена или просрочена.',
            },
            e419: {
                title: 'Страница устарела',
                desc: 'Срок действия страницы истек. Это могло произойти из-за долгого бездействия. Пожалуйста, обновите страницу.',
            },
            e429: {
                title: 'Слишком много запросов',
                desc: 'Пожалуйста, подождите немного, прежде чем продолжить.',
            },
            e500: {
                title: 'Ошибка сервера',
                desc: 'Что-то пошло не так на нашей кухне. Мы уже разбираемся с этим, попробуйте позже.',
            },
            e503: {
                title: 'Сервис недоступен',
                desc: 'Мы проводим техническое обслуживание оборудования. Скоро вернемся!',
            },
            unknown: {
                title: 'Неизвестная ошибка',
                desc: 'Произошла непредвиденная ошибка.',
            },
        },
        footer: {
            desc: 'Ваш надежный проводник в мир крафтовой культуры. Мы отбираем только лучшие сорта от проверенных пивоварен со всего мира.',
            menu: 'Меню',
            contacts: 'Контакты',
            newsletter: 'Рассылка',
            newsletterDesc:
                'Подпишитесь на наши новости, чтобы первыми узнавать о новинках и закрытых распродажах.',
            rights: 'Все права защищены.',
            privacy: 'Политика конфиденциальности',
            offer: 'Публичная оферта',
            warning:
                'Чрезмерное употребление алкоголя вредит вашему здоровью. 18+',
            warningSub:
                'Сайт содержит информацию, не рекомендованную для лиц, не достигших совершеннолетия.',
            delivery: 'Доставка и оплата',
        },
    },
    en: {
        nav: {
            catalog: 'Catalog',
            about: 'About',
            contacts: 'Contacts',
            profile: 'Profile',
            login: 'Login',
            cart: 'Cart',
        },
        common: {
            searchPlaceholder: 'Search by name...',
            found: 'Found',
            items: 'items',
            reset: 'Reset',
            buy: 'Buy',
            inCart: 'In Cart',
            outOfStock: 'Out of Stock',
            new: 'New',
            loading: 'Loading...',
            save: 'Save',
            saved: 'Saved',
            cancel: 'Cancel',
            currency: 'RUB',
            goHome: 'Go HomePage',
            tryAgain: 'Try Again',
            city: 'City',
            edit: 'Edit',
            phone: 'Phone',
            address: 'Address',
            last_name: 'Last Name',
            middle_name: 'Middle Name',
            first_name: 'First Name',
            company: 'Company',
            inn: 'INN',
            kpp: 'KPP',
            email: 'Email',
            close: 'Close',
            comment_order: 'Comment on the order',
            password: 'Password',
            password_confirmation: 'Confirm password',
            back: 'Back',
            addToCart: 'Add to Cart',
            reviews: 'Reviews',
            specifications: 'Specifications',
            description: 'Description',
            similarProducts: 'Similar Products',
        },

        product: {
            brand: 'Brand',
            style: 'Style',
            abv: 'ABV',
            ibu: 'IBU',
            plato: 'Plato',
            volume: 'Volume',
            quantity: 'Quantity',
            total: 'Total',
            writeReview: 'Write a Review',
            noReviews: 'No reviews yet',
        },
        filters: {
            title: 'Filters',
            price: 'Price',
            abv: 'Strength (ABV)',
            ibu: 'Bitterness (IBU)',
            category: 'Category',
            manufacturer: 'Vendor',
            style: 'Style',
            resetFilters: 'Reset Filters',
            search: 'Search...',
            nothingFound: 'Nothing found',
        },
        sort: {
            created_at: 'Newest first',
            priceAZ: 'Price: Low to High',
            priceZA: 'Price: High to Low',
            titleAZ: 'A-Z',
            titleZA: 'Z-A',
        },
        profile: {
            personalInformation: 'Personal information',
            managePersonalDataAndAddress:
                'Manage your personal data and delivery address',
            viewPastPurchaseDetails: 'View details of your past purchases',
            stats: {
                totalOrders: 'Total Orders',
                totalSpent: 'Total Spent',
                avgCheck: 'Avg Check',
                lastOrder: 'Last Order',
            },
        },
        auth: {
            loginTitle: 'Sign In',
            registerTitle: 'Sign Up',
            emailLabel: 'Email',
            passwordLabel: 'Password',
            nameLabel: 'Name',
            confirmPasswordLabel: 'Confirm Password',
            loginButton: 'Sign In',
            registerButton: 'Create Account',
            noAccount: "Don't have an account?",
            hasAccount: 'Already have an account?',
            toRegister: 'Sign Up',
            toLogin: 'Sign In',
            rememberMe: 'Remember me',
            forgotPassword: 'Forgot password?',
            resetPasswordTitle: 'Reset Password',
            resetPasswordDesc: 'Enter email to receive reset link',
            sendResetLink: 'Send Link',
            backToLogin: 'Back to Login',
            resetLinkSent: 'Link sent!',
            ResendVerificationEmail: 'Resend verification email',
            yourPassword: 'Your password',
            setNewPasswordDesc: 'Please enter your new password below',
            newPasswordLabel: 'Password',
            setNewPasswordBtn: 'Reset password',
            resetSuccess: 'Password changed successfully',
            setNewPasswordTitle: 'Reset password',
            errors: {
                required: 'Required field',
                email: 'Invalid Email',
                passwordShort: 'Minimum 6 characters',
                match: 'Passwords do not match',
                fail: 'Auth failed',
            },
        },
        errors: {
            e404: {
                title: 'Page Not Found',
                desc: 'Looks like this beer is already gone. The page you are looking for does not exist.',
            },
            e403: {
                title: 'Access Denied',
                desc: 'You do not have access to this page. The link may be damaged or expired.',
            },
            e419: {
                title: 'Page Expired',
                desc: 'The page has expired due to inactivity. Please refresh and try again.',
            },
            e429: {
                title: 'Too Many Requests',
                desc: 'Slow down! You are making too many requests. Please wait a bit.',
            },
            e500: {
                title: 'Server Error',
                desc: 'Something went wrong in our kitchen. We are fixing it, please try again later.',
            },
            e503: {
                title: 'Service Unavailable',
                desc: 'We are performing maintenance on our equipment. We will be back soon!',
            },
            unknown: {
                title: 'Unknown Error',
                desc: 'An unexpected error occurred.',
            },
        },
        footer: {
            desc: 'Your reliable guide to the world of craft culture. We select only the best varieties from trusted vendors around the world.',
            menu: 'Menu',
            contacts: 'Contacts',
            newsletter: 'Newsletter',
            newsletterDesc:
                'Subscribe to our news to be the first to know about new arrivals and private sales.',
            rights: 'All rights reserved.',
            privacy: 'Privacy Policy',
            offer: 'Public Offer',
            warning: 'Excessive alcohol consumption harms your health. 18+',
            warningSub:
                'The site contains information not recommended for persons under the age of majority.',
            delivery: 'Delivery & Payment',
        },
    },
};

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (path: string, defaultValue?: string | undefined) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
    undefined,
);

export function LanguageProvider({ children }: { children: ReactNode }) {
    // Initialize state lazily to avoid double render and useEffect warning
    const [language, setLanguage] = useState<Language>(() => {
        if (typeof window !== 'undefined') {
            const savedLang = localStorage.getItem(
                'beer_dealer_lang',
            ) as Language;
            if (savedLang && (savedLang === 'ru' || savedLang === 'en')) {
                return savedLang;
            }
        }
        return 'ru';
    });

    // Save language whenever it changes
    useEffect(() => {
        localStorage.setItem('beer_dealer_lang', language);
    }, [language]);

    const t = useCallback(
        (path: string, defaultValue: string | undefined): string => {
            const keys = path.split('.');
            let current: TranslationNode = translations[language];

            for (const key of keys) {
                if (typeof current !== 'object' || current === null) {
                    console.warn(`Translation missing for key: ${path}`);
                    return path;
                }

                const next: TranslationNode | undefined = (
                    current as Record<string, TranslationNode>
                )[key];

                if (next === undefined) {
                    console.warn(`Translation missing for key: ${path}`);
                    return defaultValue ?? path;
                }

                current = next;
            }

            return current as string;
        },
        [language],
    );

    const value = useMemo(
        () => ({ language, setLanguage, t }),
        [language, setLanguage, t],
    );

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}
